import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { API } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Loader2, LogOut, Download, Search, Ban, RotateCcw, X, Plus, KeyRound } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICES, SERVICE_ORDER } from "@/lib/constants";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_STYLE = {
  confirmed: "bg-green-100 text-green-900",
  initiated: "bg-amber-100 text-amber-900",
  cancelled: "bg-red-100 text-red-900",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [search, setSearch] = useState("");
  const [calDate, setCalDate] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [serviceSlots, setServiceSlots] = useState({});
  const [editService, setEditService] = useState("individual");
  const [slots, setSlots] = useState([]);
  const [capacity, setCapacity] = useState(1);
  const [newSlot, setNewSlot] = useState("");
  const [savingSlots, setSavingSlots] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [refundWindow, setRefundWindow] = useState(48);
  const [savingWindow, setSavingWindow] = useState(false);
  const [payments, setPayments] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [savingReviews, setSavingReviews] = useState(false);

  const switchEditService = (svc) => {
    setEditService(svc);
    setSlots(serviceSlots[svc]?.time_slots || []);
    setCapacity(serviceSlots[svc]?.slot_capacity || 1);
  };

  const authHeaders = () => {
    const t = localStorage.getItem("rr_admin_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    try {
      const [b, a] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/availability`),
      ]);
      setBookings(b.data.bookings);
      setBlocked(a.data.blocked_dates);
      setServiceSlots((prev) => {
        if (!Object.keys(prev).length) {
          setSlots(a.data.service_slots?.[editService]?.time_slots || []);
          setCapacity(a.data.service_slots?.[editService]?.slot_capacity || 1);
        }
        return a.data.service_slots || {};
      });
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { headers: authHeaders(), withCredentials: true });
        setUser(res.data);
        await load();
        axios.get(`${API}/availability`).then((r) => {
          if (r.data.cancellation) setRefundWindow(r.data.cancellation.refund_window_hours);
        }).catch(() => {});
        axios.get(`${API}/admin/payments-status`, { headers: authHeaders(), withCredentials: true })
          .then((r) => setPayments(r.data)).catch(() => {});
        axios.get(`${API}/testimonials`).then((r) => setReviews(r.data.items || [])).catch(() => {});
      } catch {
        navigate("/admin/login", { replace: true });
      } finally {
        setChecking(false);
      }
    })();
  }, [navigate, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bookings;
    return bookings.filter((b) =>
      [b.name, b.email, b.service_name, b.date, b.phone]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [bookings, search]);

  const upcoming = useMemo(
    () => filtered.filter((b) => b.date >= format(new Date(), "yyyy-MM-dd")),
    [filtered]
  );

  const exportCsv = () => {
    const rows = [
      ["Date", "Time", "Service", "Name", "Email", "Phone", "Students", "Total", "Status"],
      ...filtered.map((b) => [
        b.date, b.details?.time_slot || "", b.service_name, b.name, b.email, b.phone || "",
        b.num_students, `$${(b.total_cents / 100).toFixed(2)}`, b.status,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `bookings-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleBlock = async () => {
    if (!calDate) return;
    const key = format(calDate, "yyyy-MM-dd");
    setSaving(true);
    try {
      if (blocked.includes(key)) {
        await axios.delete(`${API}/admin/blocked-dates/${key}`, { headers: authHeaders(), withCredentials: true });
        setBlocked(blocked.filter((d) => d !== key));
        toast.success(`${key} is bookable again.`);
      } else {
        await axios.post(`${API}/admin/blocked-dates`, { date: key }, { headers: authHeaders(), withCredentials: true });
        setBlocked([...blocked, key]);
        toast.success(`${key} blocked — parents can't book it.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update that date.");
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    localStorage.removeItem("rr_admin_token");
    navigate("/admin/login", { replace: true });
  };

  const saveSlots = async () => {
    if (!slots.length) { toast.error("Keep at least one time slot."); return; }
    setSavingSlots(true);
    try {
      const res = await axios.put(`${API}/admin/settings/availability`,
        { service: editService, time_slots: slots, slot_capacity: capacity },
        { headers: authHeaders(), withCredentials: true });
      setServiceSlots(res.data.services);
      toast.success(`Time slots saved for ${SERVICES[editService].name} — booking page updated.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save slots.");
    } finally {
      setSavingSlots(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/admin/bookings/${cancelTarget.booking_id}/cancel`, {},
        { headers: authHeaders(), withCredentials: true });
      toast.success(res.data.refunded
        ? "Booking cancelled and payment refunded. The parent has been emailed."
        : "Booking cancelled. The parent has been emailed.");
      setCancelTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed.");
    } finally {
      setCancelling(false);
    }
  };

  const changePassword = async () => {
    if (pw.next.length < 8) { toast.error("New password needs at least 8 characters."); return; }
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match."); return; }
    setSavingPw(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: pw.current, new_password: pw.next },
        { headers: authHeaders(), withCredentials: true });
      toast.success("Password changed.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change password.");
    } finally {
      setSavingPw(false);
    }
  };

  const saveRefundWindow = async () => {
    setSavingWindow(true);
    try {
      await axios.put(`${API}/admin/settings/cancellation`,
        { refund_window_hours: refundWindow },
        { headers: authHeaders(), withCredentials: true });
      toast.success(`Parents now get a full refund up to ${refundWindow} hours before their session.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the refund window.");
    } finally {
      setSavingWindow(false);
    }
  };

  const saveReviews = async () => {
    const items = reviews.filter((r) => r.quote.trim() && r.name.trim());
    setSavingReviews(true);
    try {
      await axios.put(`${API}/admin/testimonials`, { items }, { headers: authHeaders(), withCredentials: true });
      setReviews(items);
      toast.success(items.length ? "Reviews saved — they're live on the homepage." : "Reviews cleared — the section is hidden.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save reviews.");
    } finally {
      setSavingReviews(false);
    }
  };

  if (checking) {
    return (
      <main className="min-h-screen bg-[#EBEBFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A243B]" />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#EBEBFA]" data-testid="admin-dashboard">
      <header className="bg-[#1A243B] text-[#EBEBFA]">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <p className="font-display font-bold text-lg">Ready &amp; Right — bookings</p>
            <p className="text-[#EBEBFA]/60 text-xs">Signed in as {user?.email}</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-view-site" className="text-sm text-[#EBEBFA]/80 hover:text-[#EBEBFA]">View site</Link>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 text-sm font-semibold text-[#FFC107] hover:opacity-80">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        {payments && !payments.live && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="payments-banner">
            <p className="text-sm text-amber-900">
              <span className="font-bold">Payments are in test mode.</span> No real money moves yet. To go live, claim your Stripe account in Manage → Payments (or the Stripe email), then complete the business details Stripe asks for.
            </p>
          </div>
        )}
        {payments?.live && (
          <div className="bg-green-50 border border-green-200 rounded-2xl px-6 py-4" data-testid="payments-banner">
            <p className="text-sm text-green-900"><span className="font-bold">Payments are live.</span> Real card payments are being collected.</p>
          </div>
        )}
        <section className="bg-white rounded-3xl shadow-sm p-6 md:p-8" data-testid="admin-bookings-panel">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <h2 className="font-display text-2xl text-[#1A243B]">Bookings</h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#2D3748]/50" />
                <Input
                  data-testid="admin-search-input"
                  placeholder="Search name, email, service…"
                  className="pl-9 bg-white border-[#1A243B]/15 rounded-xl h-11 w-64"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button data-testid="admin-export-btn" onClick={exportCsv} className="cta-pill flex items-center gap-2 bg-[#FFC107] text-[#1A243B] text-sm font-bold px-5 py-2.5 rounded-full">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>
          </div>

          <p className="text-sm text-[#2D3748] mb-4" data-testid="admin-bookings-count">
            {upcoming.length} upcoming · {filtered.length} shown · {bookings.length} total
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-bookings-table">
              <thead>
                <tr className="text-left text-[#2D3748]/70 border-b border-[#1A243B]/10">
                  <th className="pb-3 pr-4 font-semibold">Date</th>
                  <th className="pb-3 pr-4 font-semibold">Time</th>
                  <th className="pb-3 pr-4 font-semibold">Service</th>
                  <th className="pb-3 pr-4 font-semibold">Name</th>
                  <th className="pb-3 pr-4 font-semibold">Contact</th>
                  <th className="pb-3 pr-4 font-semibold">Total</th>
                  <th className="pb-3 pr-4 font-semibold">Status</th>
                  <th className="pb-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr key={b.booking_id} data-testid={`booking-row-${b.booking_id.slice(0, 8)}`} className="border-b border-[#1A243B]/5 align-top">
                    <td className="py-3 pr-4 font-semibold text-[#1A243B] whitespace-nowrap">
                      {format(parseISO(b.date), "MMM d, yyyy")}
                    </td>
                    <td className="py-3 pr-4 text-[#2D3748] whitespace-nowrap">{b.details?.time_slot || "—"}</td>
                    <td className="py-3 pr-4 text-[#2D3748]">{b.service_name}{b.num_students > 1 ? ` (x${b.num_students})` : ""}</td>
                    <td className="py-3 pr-4 text-[#1A243B] font-semibold">{b.name}</td>
                    <td className="py-3 pr-4 text-[#2D3748]">
                      <div>{b.email}</div>
                      {b.phone && <div className="text-[#2D3748]/70">{b.phone}</div>}
                    </td>
                    <td className="py-3 pr-4 text-[#1A243B] font-semibold whitespace-nowrap">${(b.total_cents / 100).toFixed(2)}</td>
                    <td className="py-3">
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${STATUS_STYLE[b.status] || "bg-gray-100 text-gray-700"}`} data-testid="booking-status">
                        {b.status}
                      </span>
                    </td>
                    <td className="py-3 pl-3 text-right">
                      {b.status !== "cancelled" && (
                        <button
                          data-testid={`cancel-booking-${b.booking_id.slice(0, 8)}`}
                          onClick={() => setCancelTarget(b)}
                          className="text-xs font-bold text-red-700 hover:text-red-900 border border-red-200 hover:border-red-400 rounded-full px-3 py-1.5 transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center text-[#2D3748]/60" data-testid="bookings-empty">No bookings match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="bg-white rounded-3xl shadow-sm p-6 md:p-8" data-testid="admin-availability-panel">
          <h2 className="font-display text-2xl text-[#1A243B] mb-2">Availability</h2>
          <p className="text-sm text-[#2D3748] mb-6">Pick a day, then block it (parents won't be able to book it) or unblock it again.</p>
          <div className="flex flex-col md:flex-row gap-8">
            <Calendar
              mode="single"
              selected={calDate}
              onSelect={setCalDate}
              modifiers={{ blocked: blocked.map((d) => parseISO(d)) }}
              modifiersStyles={{ blocked: { textDecoration: "line-through", color: "#b91c1c" } }}
              data-testid="admin-calendar"
              className="rounded-xl border border-[#1A243B]/10 w-fit"
            />
            <div className="flex-1">
              {calDate ? (
                <div>
                  <p className="font-display text-lg text-[#1A243B] mb-1" data-testid="admin-selected-date">
                    {format(calDate, "EEEE, MMMM d, yyyy")}
                  </p>
                  <p className="text-sm text-[#2D3748] mb-5">
                    {blocked.includes(format(calDate, "yyyy-MM-dd")) ? "This day is currently blocked." : "This day is currently bookable."}
                  </p>
                  <button
                    data-testid="admin-toggle-block-btn"
                    onClick={toggleBlock}
                    disabled={saving}
                    className={`cta-pill flex items-center gap-2 font-bold px-6 py-3 rounded-full text-sm disabled:opacity-60 ${
                      blocked.includes(format(calDate, "yyyy-MM-dd"))
                        ? "bg-[#1A243B] text-[#FFC107]"
                        : "bg-red-600 text-white"
                    }`}
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : blocked.includes(format(calDate, "yyyy-MM-dd")) ? <RotateCcw className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                    {blocked.includes(format(calDate, "yyyy-MM-dd")) ? "Unblock this day" : "Block this day"}
                  </button>
                </div>
              ) : (
                <p className="text-[#2D3748]/60 text-sm">Select a date on the calendar.</p>
              )}
              {blocked.length > 0 && (
                <div className="mt-8">
                  <p className="text-sm font-bold text-[#1A243B] mb-3">Blocked days</p>
                  <div className="flex flex-wrap gap-2" data-testid="blocked-list">
                    {blocked.sort().map((d) => (
                      <span key={d} data-testid={`blocked-${d}`} className="bg-red-50 text-red-900 text-sm font-semibold px-4 py-2 rounded-full">
                        {format(parseISO(d), "MMM d, yyyy")}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-[#1A243B]/10 mt-8 pt-8" data-testid="slot-settings">
            <h3 className="font-display text-xl text-[#1A243B] mb-2">Time slots per service</h3>
            <p className="text-sm text-[#2D3748] mb-5">Each service gets its own times and capacity. These are what parents pick from.</p>
            <div className="max-w-xs mb-6">
              <Select value={editService} onValueChange={switchEditService}>
                <SelectTrigger data-testid="slot-service-select" className="bg-white border-[#1A243B]/15 rounded-xl h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_ORDER.map((k) => (
                    <SelectItem key={k} value={k} data-testid={`slot-service-${k}`}>{SERVICES[k].name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="slot-list">
              {slots.map((s) => (
                <span key={s} className="flex items-center gap-2 bg-[#EBEBFA] text-[#1A243B] text-sm font-semibold pl-4 pr-2 py-2 rounded-full" data-testid={`slot-chip-${s.replace(/[\s:]/g, "-").toLowerCase()}`}>
                  {s}
                  <button
                    type="button"
                    aria-label={`Remove ${s}`}
                    data-testid={`slot-remove-${s.replace(/[\s:]/g, "-").toLowerCase()}`}
                    onClick={() => setSlots(slots.filter((x) => x !== s))}
                    className="w-6 h-6 rounded-full hover:bg-[#1A243B]/10 flex items-center justify-center transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
              <div className="flex items-center gap-2">
                <Input
                  data-testid="new-slot-input"
                  placeholder="e.g. 5:30 PM"
                  className="bg-white border-[#1A243B]/15 rounded-xl h-10 w-32 text-sm"
                  value={newSlot}
                  onChange={(e) => setNewSlot(e.target.value)}
                />
                <button
                  type="button"
                  data-testid="add-slot-btn"
                  onClick={() => {
                    const v = newSlot.trim();
                    if (v && !slots.includes(v)) { setSlots([...slots, v]); setNewSlot(""); }
                  }}
                  className="w-10 h-10 rounded-full bg-[#1A243B] text-[#FFC107] flex items-center justify-center hover:opacity-80 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <p className="text-sm font-bold text-[#1A243B] mb-2">
                  {editService === "group" ? "Students per slot" : "Bookings per slot"}
                </p>
                <Input
                  data-testid="capacity-input"
                  type="number"
                  min={1}
                  max={100}
                  className="bg-white border-[#1A243B]/15 rounded-xl h-11 w-28"
                  value={capacity}
                  onChange={(e) => setCapacity(Math.max(1, parseInt(e.target.value || "1", 10)))}
                />
              </div>
              <button
                data-testid="save-slots-btn"
                onClick={saveSlots}
                disabled={savingSlots}
                className="cta-pill flex items-center gap-2 bg-[#FFC107] text-[#1A243B] text-sm font-bold px-6 py-3 rounded-full disabled:opacity-60"
              >
                {savingSlots && <Loader2 className="w-4 h-4 animate-spin" />}
                Save {SERVICES[editService].name} slots
              </button>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-3xl shadow-sm p-6 md:p-8" data-testid="reviews-panel">
          <h3 className="font-display text-xl text-[#1A243B] mb-2">Family reviews</h3>
          <p className="text-sm text-[#2D3748] mb-6">
            Add quotes from families here — they appear under the hero on the homepage. Until you add at least one, the section stays hidden.
          </p>
          <div className="space-y-5">
            {reviews.map((r, i) => (
              <div key={i} className="border border-[#1A243B]/10 rounded-2xl p-5 space-y-3" data-testid={`review-editor-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <p className="text-sm font-bold text-[#1A243B]">Review {i + 1}</p>
                  <button
                    type="button"
                    data-testid={`review-remove-${i}`}
                    onClick={() => setReviews(reviews.filter((_, j) => j !== i))}
                    className="text-red-700 hover:text-red-900"
                    aria-label={`Remove review ${i + 1}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <textarea
                  data-testid={`review-quote-${i}`}
                  placeholder="What the family said…"
                  className="w-full bg-white border border-[#1A243B]/15 rounded-xl p-3 text-base min-h-[90px]"
                  value={r.quote}
                  onChange={(e) => setReviews(reviews.map((x, j) => j === i ? { ...x, quote: e.target.value } : x))}
                />
                <Input
                  data-testid={`review-name-${i}`}
                  placeholder="Who said it — e.g. Sarah M., Beaverton"
                  className="bg-white border-[#1A243B]/15 rounded-xl h-11"
                  value={r.name}
                  onChange={(e) => setReviews(reviews.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-6">
            {reviews.length < 5 && (
              <button
                type="button"
                data-testid="add-review-btn"
                onClick={() => setReviews([...reviews, { quote: "", name: "" }])}
                className="flex items-center gap-2 text-sm font-bold text-[#1A243B] border border-[#1A243B]/20 hover:border-[#1A243B]/50 rounded-full px-5 py-2.5 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add a review
              </button>
            )}
            <button
              data-testid="save-reviews-btn"
              onClick={saveReviews}
              disabled={savingReviews}
              className="cta-pill flex items-center gap-2 bg-[#FFC107] text-[#1A243B] text-sm font-bold px-6 py-3 rounded-full disabled:opacity-60"
            >
              {savingReviews && <Loader2 className="w-4 h-4 animate-spin" />}
              Save reviews
            </button>
          </div>
        </section>

        <section className="bg-white rounded-3xl shadow-sm p-6 md:p-8 max-w-xl" data-testid="refund-policy-panel">
          <h3 className="font-display text-xl text-[#1A243B] mb-2">Cancellation policy</h3>
          <p className="text-sm text-[#2D3748] mb-5">
            Parents can cancel from their confirmation email link. Cancellations made more than this many hours before the session are refunded in full automatically.
          </p>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <p className="text-sm font-bold text-[#1A243B] mb-2">Full refund up to (hours before)</p>
              <Input
                data-testid="refund-window-input"
                type="number"
                min={0}
                max={720}
                className="bg-white border-[#1A243B]/15 rounded-xl h-11 w-28"
                value={refundWindow}
                onChange={(e) => setRefundWindow(Math.max(0, parseInt(e.target.value || "0", 10)))}
              />
            </div>
            <button
              data-testid="save-refund-window-btn"
              onClick={saveRefundWindow}
              disabled={savingWindow}
              className="cta-pill flex items-center gap-2 bg-[#FFC107] text-[#1A243B] text-sm font-bold px-6 py-3 rounded-full disabled:opacity-60"
            >
              {savingWindow && <Loader2 className="w-4 h-4 animate-spin" />}
              Save policy
            </button>
          </div>
        </section>

        <section className="bg-white rounded-3xl shadow-sm p-6 md:p-8 max-w-xl" data-testid="password-panel">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-10 h-10 rounded-full bg-[#EBEBFA] flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-[#1A243B]" />
            </span>
            <h3 className="font-display text-xl text-[#1A243B]">Change password</h3>
          </div>
          <p className="text-sm text-[#2D3748] mb-5">Set a new password for this owner account.</p>
          <div className="space-y-4">
            <Input
              data-testid="pw-current-input"
              type="password"
              placeholder="Current password"
              className="bg-white border-[#1A243B]/15 rounded-xl h-12"
              value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
            />
            <Input
              data-testid="pw-new-input"
              type="password"
              placeholder="New password (8+ characters)"
              className="bg-white border-[#1A243B]/15 rounded-xl h-12"
              value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
            />
            <Input
              data-testid="pw-confirm-input"
              type="password"
              placeholder="Repeat new password"
              className="bg-white border-[#1A243B]/15 rounded-xl h-12"
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            />
            <button
              data-testid="change-password-btn"
              onClick={changePassword}
              disabled={savingPw || !pw.current || !pw.next}
              className="cta-pill flex items-center gap-2 bg-[#FFC107] text-[#1A243B] text-sm font-bold px-6 py-3 rounded-full disabled:opacity-60"
            >
              {savingPw && <Loader2 className="w-4 h-4 animate-spin" />}
              Change password
            </button>
          </div>
        </section>
      </div>

      <AlertDialog open={!!cancelTarget} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <AlertDialogContent data-testid="cancel-dialog" className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A243B]">Cancel this booking?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget && (
                <>
                  {cancelTarget.service_name} for {cancelTarget.name} on {format(parseISO(cancelTarget.date), "MMM d, yyyy")}.
                  {cancelTarget.payment_status === "paid"
                    ? ` The parent will be refunded $${(cancelTarget.total_cents / 100).toFixed(2)} and emailed.`
                    : " No payment was taken. The parent will be emailed."}
                  {" "}The time slot opens up again.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-dialog-no" className="rounded-full">Keep booking</AlertDialogCancel>
            <AlertDialogAction
              data-testid="cancel-dialog-yes"
              onClick={confirmCancel}
              disabled={cancelling}
              className="rounded-full bg-red-600 text-white hover:bg-red-700"
            >
              {cancelling ? "Cancelling…" : "Yes, cancel & refund"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
